import type { ReactNode } from "react";
import { Box } from "../../../components/ui/box";
import { Text } from "../../../components/ui/text";
import type { Omittable } from "../../../lib/props";
import { WidgetBandHeader } from "../widget-band-header";

/**
 * The empty state of a BANDED list: the bands the body would have drawn,
 * each counting zero, instead of a title alone. Zeros are data — a
 * reader who opens the card sees the same rows they will see once it fills,
 * so nothing shifts under them when the first row lands.
 *
 * Each band is `WidgetBandHeader` drawn `muted`. A band that carries
 * `onToggle` is the same disclosure a live band is — caret, capsule,
 * "Collapse Due soon" — so the empty card and the full card share one
 * geometry and a reader's fold gesture works before a row exists. `note` is
 * the one faint line under the bands that says why they stand empty;
 * `action` is the one thing the reader can do about it.
 */
export type WidgetEmptyBand = Omittable<{
  key: string;
  label: ReactNode;
  /** Whether the band reads as open. Read only with `onToggle`. */
  expanded?: boolean;
  /** Fold or unfold the band. Absent, the header is a plain heading. */
  onToggle?: () => void;
  /** The band's spoken name for the toggle when `label` is not a string. */
  name?: string;
}>;

export type WidgetEmptyBandsProps = Omittable<{
  bands: readonly WidgetEmptyBand[];
  note?: string;
  action?: ReactNode;
}>;

export function WidgetEmptyBands({ bands, note, action }: WidgetEmptyBandsProps) {
  return (
    <Box direction="column" shrink={false}>
      {bands.map((band) => (
        <WidgetBandHeader
          key={band.key}
          count={0}
          label={band.label}
          muted
          {...(band.expanded === undefined ? {} : { expanded: band.expanded })}
          {...(band.onToggle ? { onToggle: band.onToggle } : {})}
          {...(band.name === undefined ? {} : { name: band.name })}
        />
      ))}
      {note ? (
        <Box paddingTop="sm">
          <Text size="xs" variant="faint">
            {note}
          </Text>
        </Box>
      ) : null}
      {action ? (
        <Box direction="row" paddingTop="sm">
          {action}
        </Box>
      ) : null}
    </Box>
  );
}
